import { generatePath } from 'react-router-dom';
import { IconEye } from '@tabler/icons-react';
import { useJournalId } from 'hooks/useJournalId/useJournalId';
import { AppTable } from 'components/organisms';
import { commonTexts, journalTexts } from 'consts/text';
import { appPaths } from 'consts/paths/paths';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import { Option } from 'components/atoms';
import { useSearchParamsBasedPagination } from 'hooks/useSearchParamsBasedPagination/useSearchParamsBasedPagination';
import { AutomaticCheckJournalBasicDTO } from 'types/automaticCheckJournal';
import { JournalPagePropsWithJournalType } from 'pages/app/staff/journal/journalPagePropsWithJournalType';
import { useJournalDetailsBreadcrumb } from 'pages/app/staff/journal/journalDetails/hooks';
import { columns } from './AutomaticCheckJournalPage.utils';
import { useAutomaticCheckJournalPageNetworkManage } from './hooks/useAutomaticCheckJournalPageNetworkManage';

export const AutomaticCheckJournalPage = ({ journalType }: JournalPagePropsWithJournalType) => {
    const { journalId } = useJournalId();
    const { statefulNavigate } = useNavigateWithState();
    // TODO: Uncomment after implementing Auto Checking Journals
    // const { genericAbilities } = useJournalAbilities();
    const { currentPage, currentPageSize, handlePaginationValuesChange } =
        useSearchParamsBasedPagination();

    useJournalDetailsBreadcrumb({
        journalId,
        journalType,
    });

    const {
        automaticCheckJournalData,
        isAutomaticCheckJournalDataError,
        isAutomaticCheckJournalDataInitialLoading,
        isAutomaticCheckJournalDataRefetching,
        automaticCheckJournalPagination,
    } = useAutomaticCheckJournalPageNetworkManage({
        journalId,
        pagination: {
            currentPage,
            currentPageSize,
            onPaginationValuesChange: handlePaginationValuesChange,
        },
    });
    const isGroupJournal = journalType === 'GROUP';

    const navigateToCheckJournalAddPage = () => {
        statefulNavigate(
            generatePath(
                isGroupJournal
                    ? appPaths.app.journals.groupJournalDetails.pickedJournal.automaticCheckJournal
                          .add
                    : appPaths.app.journals.specialJournalDetails.pickedJournal
                          .automaticCheckJournal.add,
                { journalId },
            ),
        );
    };

    const navigateToCheckJournalDetailsPage = (
        automaticCheckJournalId: AutomaticCheckJournalBasicDTO['id'],
    ) => {
        statefulNavigate(
            generatePath(
                isGroupJournal
                    ? appPaths.app.journals.groupJournalDetails.pickedJournal.automaticCheckJournal
                          .details.pickedAutomaticCheckJournal
                    : appPaths.app.journals.specialJournalDetails.pickedJournal
                          .automaticCheckJournal.details.pickedAutomaticCheckJournal,
                {
                    journalId,
                    automaticCheckJournalId,
                },
            ),
        );
    };

    // const canViewCheckJournalDetails = genericAbilities.checkIfCanViewAutomaticCheckJournal();

    const options: Option<AutomaticCheckJournalBasicDTO>[] = [
        {
            label: commonTexts.actionLabels.details,
            onClick: ({ id }) => navigateToCheckJournalDetailsPage(id),
            Icon: <IconEye />,
            // TODO: Uncomment after implementing Auto Checking Journals
            // checkIfVisible: () => canViewCheckJournalDetails,
        },
    ];

    return (
        <AppTable
            title={journalTexts.journalDetails.common.automaticCheckJournal.checkJournalList}
            emptyInfo={
                journalTexts.journalDetails.common.automaticCheckJournal.emptyCheckJournalList
            }
            addButtonLabel={journalTexts.journalDetails.common.automaticCheckJournal.checkJournal}
            // TODO: Uncomment after implementing Auto Checking Journals
            // isHiddenAddButton={canViewCheckJournalDetails}
            onAddButtonClick={navigateToCheckJournalAddPage}
            options={options}
            columns={columns}
            dataSource={automaticCheckJournalData ?? []}
            rowKey={({ id }) => id}
            pagination={automaticCheckJournalPagination}
            isError={isAutomaticCheckJournalDataError}
            isDataInitialLoading={isAutomaticCheckJournalDataInitialLoading}
            isDataRefetching={isAutomaticCheckJournalDataRefetching}
            isTableManageUtilsHidden
        />
    );
};
